import Link from 'next/link';
import React, { useState } from 'react';
import { IoArrowForward, IoCheckmark } from 'react-icons/io5';
import { TbArrowsExchange } from 'react-icons/tb';

import { Anchor, Box, Group, Menu, Text, Tooltip, UnstyledButton } from '@mantine/core';

import { AppName } from 'models';
import { AppHelpers } from 'helpers/AppHelpers';
import { MarketingHelpers } from 'helpers/MarketingHelpers';

import { EIcon } from 'components/logo/EIcon';
import { ReplusLogo } from 'components/logo/ReplusLogo';
import { useCurrentApp } from 'hooks/use-current-app';
import { useSnapshot } from 'hooks/use-safe-snapshot';
import { useWorkspaceConfig } from 'hooks/use-workspace-config';
import { CurrentAppService } from 'services/CurrentAppService';
import { state as currentUserState } from 'state/current-user';

import cx from './AppSwitcher.module.scss';

const AppSwitcherMenu = ({ children }: { children: React.ReactNode }) => {
    const options = useAppSwitchOptions();
    const [opened, setOpened] = useState(false);

    if (!options) return children;

    return (
        <Menu
            opened={opened}
            onChange={setOpened}
            position="bottom-start"
            width={360}
            offset={8}
            classNames={{ dropdown: cx.dropdown }}
        >
            <Menu.Target>
                <Group wrap="nowrap" gap={0}>
                    <Box display="contents" onClick={(event) => event.stopPropagation()}>
                        {children}
                    </Box>
                    <Tooltip
                        label="Switch platform"
                        disabled={opened}
                        position="bottom"
                        offset={4}
                        bg="white"
                        openDelay={150}
                        transitionProps={{ transition: 'pop', duration: 180 }}
                        classNames={{ tooltip: cx.tooltip }}
                    >
                        <UnstyledButton
                            className={cx.trigger}
                            aria-label="Switch platform"
                            aria-expanded={opened}
                            data-opened={opened || undefined}
                            data-replus={
                                options.some(({ app, active }) => active && app === AppName.REPLUS) || undefined
                            }
                        >
                            <TbArrowsExchange size={16} />
                        </UnstyledButton>
                    </Tooltip>
                </Group>
            </Menu.Target>

            <Menu.Dropdown>
                {options.map(({ app, title, description, logo, active, href, select, aboutUrl }) => {
                    const itemProps = {
                        onClick: select,
                        leftSection: (
                            <Box className={cx.tile}>
                                {logo(20)}
                                {active && (
                                    <span className={cx.activeBadge} data-replus={app === AppName.REPLUS || undefined}>
                                        <IoCheckmark size={10} />
                                    </span>
                                )}
                            </Box>
                        ),
                        'data-active': active,
                        'data-replus': app === AppName.REPLUS || undefined,
                        className: cx.menuItem,
                        children: (
                            <>
                                <Text fw={600} fz="sm" lh={1.3}>
                                    {title}
                                </Text>
                                <Box className={cx.descriptionSwap}>
                                    <Text c="dimmed" fz="xs" lh={1.4} className={cx.description}>
                                        {description}
                                    </Text>
                                    {!active && (
                                        <Text fz="xs" fw={600} lh={1.4} className={cx.switchHint} aria-hidden>
                                            Switch to {title} <IoArrowForward size={11} />
                                        </Text>
                                    )}
                                </Box>
                            </>
                        ),
                    };

                    return (
                        <Box key={app} className={cx.row}>
                            {href ? (
                                <Menu.Item component={Link} href={href} {...itemProps} />
                            ) : (
                                <Menu.Item {...itemProps} />
                            )}
                            <Anchor
                                component={Link}
                                href={aboutUrl}
                                className={cx.about}
                                onClick={() => setOpened(false)}
                            >
                                Learn more
                            </Anchor>
                        </Box>
                    );
                })}
            </Menu.Dropdown>
        </Menu>
    );
};

const AppSwitcherTabs = () => {
    const options = useAppSwitchOptions();

    if (!options) return null;

    const activeIndex = options.findIndex(({ active }) => active);

    return (
        <Box
            className={cx.tabs}
            data-active-index={activeIndex}
            data-replus={options[activeIndex]?.app === AppName.REPLUS || undefined}
        >
            {options.map(({ app, title, logo, active, href, select }) => {
                const tabProps = {
                    onClick: select,
                    className: cx.tab,
                    'data-active': active,
                    'aria-pressed': active,
                    children: (
                        <>
                            {logo(18)}
                            {title}
                        </>
                    ),
                };

                return href ? (
                    <UnstyledButton key={app} component={Link} href={href} {...tabProps} />
                ) : (
                    <UnstyledButton key={app} {...tabProps} />
                );
            })}
        </Box>
    );
};

const useAppSwitchOptions = () => {
    const { workspace } = useWorkspaceConfig();
    const currentApp = useCurrentApp();
    const { user } = useSnapshot(currentUserState);

    // TODO: let's get internal OK first
    if (workspace || !user?.internal) return null;

    const options = [
        {
            app: AppName.DCIDE,
            title: 'DCIDE',
            description: 'From single-line diagram to BOM',
            aboutUrl: MarketingHelpers.urls.dcide.overview(),
            logo: (size: number) => <EIcon height={size} />,
        },
        {
            app: AppName.REPLUS,
            title: 'RE+Source PRO',
            description: 'The #1 resource for power systems',
            aboutUrl: MarketingHelpers.urls.resourcepro.overview(),
            logo: (size: number) => <ReplusLogo h={size - 2} w="auto" />,
        },
    ];

    return options.map((option) => {
        const active = option.app === currentApp;
        const href = active ? undefined : AppHelpers.getHomeUrl(option.app, !!user);

        return {
            ...option,
            active,
            href,
            select: () => CurrentAppService.setActive(option.app, user?.id ?? null),
        };
    });
};

export { AppSwitcherMenu, AppSwitcherTabs };
